/**
 * BookingPagesManager — Integrations > FHL Scheduling > Booking Pages.
 *
 * CRUD over crm_booking_pages: each row is a public co-branded page at
 * {origin}/book/{handle}/{slug} that wraps a Cal.diy event type (cal.fhl.one).
 * The handle comes from users/{uid}.bookingHandle (set at provisioning) and
 * slugs are unique per handle, so different LOs can reuse the same slug.
 * Supports multiple pages per LO (e.g. one per realtor partnership) with
 * co-brand headshots — the LO photo syncs automatically from the Hub profile
 * (users/{uid}.photo, never uploaded here); only the partner photo is uploaded.
 * A single default page is mirrored to users/{uid}.defaultBookingUrl for the
 * {{bookingLink}} merge field, with copy-link and activate/deactivate.
 *
 * New pages pick a meeting type from a dropdown (`meetingTypes` prop, loaded
 * via calListEventTypes) — the Cal event URL is derived, never hand-pasted.
 * Scheduling mechanics (durations, availability) live in the sibling
 * Meeting Types / Availability tabs.
 *
 * Reads use the anon Supabase client; ALL writes go through the
 * calSaveBookingPage / calDeleteBookingPage callables (the table's anon-key
 * write policies are dropped — the server validates ownership, slugs, and
 * mirrors users/{uid}.defaultBookingUrl for the {{bookingLink}} merge field).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Text,
  Heading,
  HStack,
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Switch,
  Badge,
  Avatar,
  AvatarGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Tooltip,
  Spinner,
  Center,
  useToast,
  useDisclosure,
  useColorModeValue,
  useClipboard,
  Divider,
  Select,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiExternalLink,
  FiStar,
  FiUpload,
} from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { supabase } from '../../../utils/supabase';
import { firebase, app } from '../../../utils/init-firebase';
import { CAL_BASE_URL, buildPublicBookingUrl } from '../../../constants/calScheduling';

const fns = () => getFunctions(app, 'us-east1');
const call = (name, payload) => httpsCallable(fns(), name)(payload).then((r) => r.data);

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const EMPTY_FORM = {
  title: '',
  slug: '',
  cal_event_url: '',
  cal_event_type_id: null,
  headline: '',
  intro_text: '',
  lo_name: '',
  lo_title: '',
  lo_nmls: '',
  lo_headshot_url: '',
  partner_name: '',
  partner_title: '',
  partner_company: '',
  partner_headshot_url: '',
  accent_color: '#1A365D',
  is_active: true,
};

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The LO headshot is the user's Hub profile photo (Firestore users.photo,
 * mirrored from Google) — denormalized onto the row so the public page can
 * render it unauthenticated. Google avatar URLs default to a 96px thumbnail;
 * request a larger rendition for the public page.
 */
function hubHeadshotUrl(user) {
  const photo = user?.photo || user?.photoURL || '';
  if (!photo) return '';
  return photo.replace(/=s\d+(-c)?$/, '=s400-c');
}

/**
 * Upload button that puts an image in booking-pages/{uid}/ and returns the URL.
 * Partner-only — the LO headshot is synced from the Hub profile, never uploaded.
 */
function HeadshotUpload({ label, url, userId, onUploaded, toast }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Use PNG, JPEG, or WebP.', status: 'warning', duration: 3000 });
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast({ title: 'File too large', description: 'Headshots must be under 5 MB.', status: 'warning', duration: 3000 });
      return;
    }
    setUploading(true);
    try {
      const path = `booking-pages/${userId}/${Date.now()}_${file.name}`;
      const storageRef = firebase.storage().ref(path);
      await storageRef.put(file);
      const downloadUrl = await storageRef.getDownloadURL();
      onUploaded(downloadUrl);
    } catch (err) {
      console.error('Headshot upload failed:', err);
      toast({ title: 'Upload failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <HStack spacing={3}>
        <Avatar size="md" src={url || undefined} />
        <input type="file" ref={inputRef} accept={ALLOWED_TYPES.join(',')} style={{ display: 'none' }} onChange={handleFile} />
        <Button
          size="sm"
          leftIcon={uploading ? <Spinner size="xs" /> : <FiUpload />}
          onClick={() => inputRef.current?.click()}
          isDisabled={uploading}
          variant="outline"
        >
          {url ? 'Replace' : 'Upload'}
        </Button>
        {url && (
          <Button size="sm" variant="ghost" colorScheme="red" onClick={() => onUploaded('')}>
            Remove
          </Button>
        )}
      </HStack>
    </FormControl>
  );
}

/** Compact live preview of the public page's co-brand header. */
function CoBrandPreview({ form }) {
  const previewBg = useColorModeValue('gray.50', 'gray.700');
  const hasPartner = !!(form.partner_name || form.partner_headshot_url);
  return (
    <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
      <Box h="6px" style={{ backgroundColor: form.accent_color || '#1A365D' }} />
      <VStack spacing={2} py={4} px={4} bg={previewBg}>
        <AvatarGroup size="lg" max={2} spacing={hasPartner ? '-0.75rem' : 0}>
          <Avatar src={form.lo_headshot_url || undefined} name={form.lo_name || 'Loan Officer'} />
          {hasPartner && <Avatar src={form.partner_headshot_url || undefined} name={form.partner_name || 'Partner'} />}
        </AvatarGroup>
        <Text fontWeight="bold" fontSize="sm" textAlign="center">
          {form.lo_name || 'Loan Officer'}
          {hasPartner ? ` & ${form.partner_name || 'Partner'}` : ''}
        </Text>
        <Text fontSize="xs" color="gray.500" textAlign="center" noOfLines={2}>
          {form.headline || 'Schedule time with us'}
        </Text>
        {form.lo_nmls && (
          <Text fontSize="10px" color="gray.400">NMLS #{form.lo_nmls}</Text>
        )}
      </VStack>
    </Box>
  );
}

function BookingPageCard({ page, onEdit, onDelete, onSetDefault, onToggleActive }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const publicUrl = buildPublicBookingUrl(page.owner_handle, page.slug);
  const { onCopy, hasCopied } = useClipboard(publicUrl);
  const hasPartner = !!(page.partner_name || page.partner_headshot_url);

  return (
    <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} opacity={page.is_active ? 1 : 0.55}>
      <HStack justify="space-between" align="start" mb={2}>
        <HStack spacing={3}>
          <AvatarGroup size="sm" max={2}>
            <Avatar src={page.lo_headshot_url || undefined} name={page.lo_name || page.title} />
            {hasPartner && <Avatar src={page.partner_headshot_url || undefined} name={page.partner_name} />}
          </AvatarGroup>
          <Box>
            <HStack spacing={2}>
              <Text fontWeight="semibold">{page.title}</Text>
              {page.is_default && <Badge colorScheme="yellow">Default</Badge>}
              {!page.is_active && <Badge>Inactive</Badge>}
            </HStack>
            <Text fontSize="xs" color="gray.500">/book/{page.owner_handle}/{page.slug}</Text>
            {hasPartner && (
              <Text fontSize="xs" color="gray.500">
                with {page.partner_name}{page.partner_company ? ` (${page.partner_company})` : ''}
              </Text>
            )}
          </Box>
        </HStack>
      </HStack>
      <HStack spacing={1} mt={3} flexWrap="wrap">
        <Tooltip label={hasCopied ? 'Copied!' : 'Copy public link'}>
          <IconButton size="sm" variant="ghost" icon={<FiCopy />} aria-label="Copy link" onClick={onCopy} />
        </Tooltip>
        <Tooltip label="Open public page">
          <IconButton
            size="sm"
            variant="ghost"
            icon={<FiExternalLink />}
            aria-label="Open page"
            onClick={() => window.open(publicUrl, '_blank', 'noopener')}
          />
        </Tooltip>
        <Tooltip label={page.is_default ? 'This is your default page' : 'Set as default (used by {{bookingLink}})'}>
          <IconButton
            size="sm"
            variant="ghost"
            colorScheme={page.is_default ? 'yellow' : 'gray'}
            icon={<FiStar />}
            aria-label="Set default"
            onClick={() => !page.is_default && onSetDefault(page)}
          />
        </Tooltip>
        <Tooltip label="Edit">
          <IconButton size="sm" variant="ghost" icon={<FiEdit2 />} aria-label="Edit" onClick={() => onEdit(page)} />
        </Tooltip>
        <Tooltip label="Delete">
          <IconButton size="sm" variant="ghost" colorScheme="red" icon={<FiTrash2 />} aria-label="Delete" onClick={() => onDelete(page)} />
        </Tooltip>
        <Box flex="1" />
        <Tooltip
          label={
            page.is_default && page.is_active
              ? 'Your default page stays live — set another page as default first'
              : page.is_active ? 'Turn page off' : 'Turn page on'
          }
        >
          {/* Tooltip needs a wrapper to fire on a disabled control */}
          <Box>
            <Switch
              size="sm"
              isChecked={page.is_active}
              isDisabled={page.is_default && page.is_active}
              onChange={() => onToggleActive(page)}
            />
          </Box>
        </Tooltip>
      </HStack>
    </Box>
  );
}

export default function BookingPagesManager({ userId, targetUser, meetingTypes = [], calUsername = '' }) {
  const toast = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPage, setEditingPage] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const cancelDeleteRef = useRef();

  const { isOpen: isEditorOpen, onOpen: onEditorOpen, onClose: onEditorClose } = useDisclosure();
  const subtleText = useColorModeValue('gray.600', 'gray.400');

  // URL namespace segment — set on the user doc when their scheduling account
  // is provisioned. Existing rows keep their own owner_handle when edited.
  const ownerHandle = targetUser?.bookingHandle || '';

  const loadPages = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('crm_booking_pages')
      .select('*')
      .eq('owner_id', userId)
      .order('created_at', { ascending: true });
    if (error) {
      console.error('Failed to load booking pages:', error);
      toast({ title: 'Failed to load booking pages', description: error.message, status: 'error', duration: 4000 });
    } else {
      setPages(data || []);
    }
    setLoading(false);
  }, [userId, toast]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  // Dropdown pick → derive the Cal event URL (users never see raw cal URLs)
  const handleMeetingTypeChange = (e) => {
    const id = Number(e.target.value) || null;
    const meetingType = meetingTypes.find((m) => m.id === id);
    setForm((prev) => ({
      ...prev,
      cal_event_type_id: id,
      cal_event_url:
        meetingType && calUsername
          ? `${CAL_BASE_URL}/${calUsername}/${meetingType.slug}`
          : prev.cal_event_url,
    }));
  };

  const setField = (field) => (eOrValue) => {
    const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'title' && !slugTouched && !editingPage) {
        next.slug = slugify(value);
      }
      if (field === 'slug') {
        next.slug = slugify(value);
      }
      return next;
    });
    if (field === 'slug') setSlugTouched(true);
  };

  const openCreate = () => {
    setEditingPage(null);
    setSlugTouched(false);
    setForm({
      ...EMPTY_FORM,
      lo_name: targetUser?.Name || targetUser?.name || targetUser?.displayName || '',
      lo_headshot_url: hubHeadshotUrl(targetUser),
    });
    onEditorOpen();
  };

  const openEdit = (page) => {
    setEditingPage(page);
    setSlugTouched(true);
    setForm({
      title: page.title || '',
      slug: page.slug || '',
      cal_event_url: page.cal_event_url || '',
      cal_event_type_id: page.cal_event_type_id || null,
      headline: page.headline || '',
      intro_text: page.intro_text || '',
      lo_name: page.lo_name || '',
      lo_title: page.lo_title || '',
      lo_nmls: page.lo_nmls || '',
      // Re-sync from the Hub profile on every edit (falls back to whatever the
      // row already had if the user has no Hub photo).
      lo_headshot_url: hubHeadshotUrl(targetUser) || page.lo_headshot_url || '',
      partner_name: page.partner_name || '',
      partner_title: page.partner_title || '',
      partner_company: page.partner_company || '',
      partner_headshot_url: page.partner_headshot_url || '',
      accent_color: page.accent_color || '#1A365D',
      is_active: page.is_active !== false,
    });
    onEditorOpen();
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) {
      toast({ title: 'Missing required fields', description: 'Title and link slug are required.', status: 'warning', duration: 4000 });
      return;
    }
    if (!form.cal_event_url.trim()) {
      toast({
        title: 'Pick a meeting type',
        description: meetingTypes.length
          ? 'Choose which meeting type this page books.'
          : 'No meeting types found — create one in the Meeting Types tab first.',
        status: 'warning',
        duration: 5000,
      });
      return;
    }
    if (!/^https?:\/\//i.test(form.cal_event_url)) {
      toast({ title: 'Invalid event URL', description: `Paste the full event link from ${CAL_BASE_URL} (e.g. ${CAL_BASE_URL}/you/intro-call).`, status: 'warning', duration: 5000 });
      return;
    }
    const rowHandle = editingPage?.owner_handle || ownerHandle;
    if (!rowHandle) {
      toast({ title: 'Scheduling account not set up', description: 'Finish the FHL Scheduling setup first — it assigns your booking handle (the /book/you/... part of your links).', status: 'warning', duration: 6000 });
      return;
    }

    setSaving(true);
    try {
      // Server enforces ownership/slug/handle rules and mirrors defaultBookingUrl
      await call('calSaveBookingPage', {
        userId,
        pageId: editingPage?.id || undefined,
        page: { ...form },
      });
    } catch (err) {
      setSaving(false);
      const isDupe = err.code === 'functions/already-exists';
      toast({
        title: isDupe ? 'Slug already in use' : 'Save failed',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
      return;
    }
    setSaving(false);

    toast({ title: editingPage ? 'Booking page updated' : 'Booking page created', status: 'success', duration: 3000 });
    onEditorClose();
    loadPages();
  };

  const handleSetDefault = async (page) => {
    try {
      await call('calSaveBookingPage', { userId, action: 'set_default', pageId: page.id });
    } catch (err) {
      toast({ title: 'Failed to set default', description: err.message, status: 'error', duration: 4000 });
      return;
    }
    toast({ title: `"${page.title}" is now your default page`, status: 'success', duration: 3000 });
    loadPages();
  };

  const handleToggleActive = async (page) => {
    try {
      await call('calSaveBookingPage', { userId, action: 'toggle_active', pageId: page.id });
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 4000 });
      return;
    }
    loadPages();
  };

  const handleDelete = async () => {
    const page = deleteTarget;
    setDeleteTarget(null);
    if (!page) return;
    try {
      await call('calDeleteBookingPage', { userId, pageId: page.id });
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 4000 });
      return;
    }
    toast({ title: 'Booking page deleted', status: 'info', duration: 3000 });
    loadPages();
  };

  return (
    <Box>
      <HStack justify="space-between" mb={1}>
        <Box>
          <Heading size="md">Booking Pages</Heading>
          <Text fontSize="sm" color={subtleText} mt={1}>
            Public, co-branded scheduling pages powered by FHL Scheduling. Create one per partnership —
            each gets its own link you can text, email, or put on social.
          </Text>
        </Box>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate} flexShrink={0}>
          New Page
        </Button>
      </HStack>

      <Box mb={5} mt={2} />

      {loading ? (
        <Center py={10}><Spinner /></Center>
      ) : pages.length === 0 ? (
        <Center py={10} flexDir="column" borderWidth="1px" borderStyle="dashed" borderRadius="lg">
          <Text color={subtleText} mb={3}>No booking pages yet.</Text>
          <Button size="sm" leftIcon={<FiPlus />} onClick={openCreate}>Create your first page</Button>
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {pages.map((page) => (
            <BookingPageCard
              key={page.id}
              page={page}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onSetDefault={handleSetDefault}
              onToggleActive={handleToggleActive}
            />
          ))}
        </SimpleGrid>
      )}

      {/* Create / edit modal */}
      <Modal isOpen={isEditorOpen} onClose={onEditorClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingPage ? 'Edit Booking Page' : 'New Booking Page'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <VStack align="stretch" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Page title</FormLabel>
                  <Input value={form.title} onChange={setField('title')} placeholder="Intro Call with Mitchell & Sarah" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Link slug</FormLabel>
                  <Input value={form.slug} onChange={setField('slug')} placeholder="intro-call" />
                  <FormHelperText>{`${window.location.origin}/book/${editingPage?.owner_handle || ownerHandle || 'you'}/${form.slug || 'your-slug'}`}</FormHelperText>
                  {editingPage && form.slug && form.slug !== editingPage.slug && (
                    <FormHelperText color="orange.500">
                      Changing the slug changes this page's link. Links you've already shared won't break —
                      they'll show your default page instead.
                    </FormHelperText>
                  )}
                </FormControl>
                {meetingTypes.length > 0 ? (
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Meeting type</FormLabel>
                    <Select
                      placeholder={form.cal_event_url && !form.cal_event_type_id ? 'Keep current meeting link' : 'Pick a meeting type'}
                      value={form.cal_event_type_id || ''}
                      onChange={handleMeetingTypeChange}
                    >
                      {meetingTypes.map((mt) => (
                        <option key={mt.id} value={mt.id}>
                          {mt.title} · {mt.length} min
                        </option>
                      ))}
                    </Select>
                    <FormHelperText>Controls the duration and questions people see when they book.</FormHelperText>
                  </FormControl>
                ) : (
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Cal event URL</FormLabel>
                    <Input value={form.cal_event_url} onChange={setField('cal_event_url')} placeholder={`${CAL_BASE_URL}/you/intro-call`} />
                    <FormHelperText>No meeting types loaded — create one in the Meeting Types tab, or paste the event link.</FormHelperText>
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel fontSize="sm">Headline</FormLabel>
                  <Input value={form.headline} onChange={setField('headline')} placeholder="Let's talk about your home loan" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Intro text</FormLabel>
                  <Textarea value={form.intro_text} onChange={setField('intro_text')} rows={3} placeholder="Pick a time that works for you — we'll cover rates, budget, and next steps." />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Accent color</FormLabel>
                  <HStack>
                    <Input type="color" value={form.accent_color} onChange={setField('accent_color')} w="60px" p={1} />
                    <Input value={form.accent_color} onChange={setField('accent_color')} w="120px" />
                  </HStack>
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Page active</FormLabel>
                  <Switch
                    isChecked={form.is_active}
                    isDisabled={!!editingPage?.is_default}
                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                  />
                  {editingPage?.is_default && (
                    <Text fontSize="xs" color="gray.500" ml={3}>
                      Default page stays live — set another page as default first.
                    </Text>
                  )}
                </FormControl>
              </VStack>

              <VStack align="stretch" spacing={4}>
                <Heading size="xs" textTransform="uppercase" color={subtleText}>Loan Officer</Heading>
                <SimpleGrid columns={2} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Name</FormLabel>
                    <Input value={form.lo_name} onChange={setField('lo_name')} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Title</FormLabel>
                    <Input value={form.lo_title} onChange={setField('lo_title')} placeholder="Loan Officer" />
                  </FormControl>
                </SimpleGrid>
                <SimpleGrid columns={2} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">NMLS #</FormLabel>
                    <Input value={form.lo_nmls} onChange={setField('lo_nmls')} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Headshot</FormLabel>
                    <HStack spacing={3}>
                      <Avatar size="md" src={form.lo_headshot_url || undefined} name={form.lo_name || 'Loan Officer'} />
                      <Text fontSize="xs" color={subtleText}>
                        {form.lo_headshot_url
                          ? 'Synced automatically from the Hub profile photo.'
                          : 'No Hub profile photo yet — add one on the Profile page and it will appear here.'}
                      </Text>
                    </HStack>
                  </FormControl>
                </SimpleGrid>

                <Divider />
                <Heading size="xs" textTransform="uppercase" color={subtleText}>Co-Brand Partner (optional)</Heading>
                <SimpleGrid columns={2} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Name</FormLabel>
                    <Input value={form.partner_name} onChange={setField('partner_name')} placeholder="Sarah Realtor" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Title</FormLabel>
                    <Input value={form.partner_title} onChange={setField('partner_title')} placeholder="Real Estate Agent" />
                  </FormControl>
                </SimpleGrid>
                <SimpleGrid columns={2} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Company</FormLabel>
                    <Input value={form.partner_company} onChange={setField('partner_company')} placeholder="Keller Williams" />
                  </FormControl>
                  <HeadshotUpload
                    label="Headshot"
                    url={form.partner_headshot_url}
                    userId={userId}
                    toast={toast}
                    onUploaded={(url) => setForm((p) => ({ ...p, partner_headshot_url: url }))}
                  />
                </SimpleGrid>

                <Divider />
                <Heading size="xs" textTransform="uppercase" color={subtleText}>Preview</Heading>
                <CoBrandPreview form={form} />
              </VStack>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditorClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
              {editingPage ? 'Save Changes' : 'Create Page'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirm */}
      <AlertDialog isOpen={!!deleteTarget} leastDestructiveRef={cancelDeleteRef} onClose={() => setDeleteTarget(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Booking Page</AlertDialogHeader>
            <AlertDialogBody>
              Delete "{deleteTarget?.title}"?
              {pages.length <= 1
                ? ' This is your only booking page — everyone who has your link will see "page unavailable" until you create a new page.'
                : deleteTarget?.is_default
                  ? ' This is your default page — your oldest remaining active page will become the new default, and links to this one will show it instead.'
                  : ' Links you\u2019ve shared to this page won\u2019t break — they\u2019ll show your default page instead.'}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
